import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, Clock } from 'lucide-react';
import { useApp } from '../../../contexts/AppContext';
import { useToast } from '../../ui/Toast';
import { Card, CardContent, CardHeader } from '../../ui/Card';
import Button from '../../ui/Button';
import { describeSchedule, getScheduleDays } from '../../../utils/autoSyncSchedule';
import { autoSyncGate, planErrorMessage } from '../../../billing/pricingV2';
import { usePricingV2 } from '../../../hooks/usePricingV2';

const DAYS = [
  { key: 'sunday', short: 'S' },
  { key: 'monday', short: 'M' },
  { key: 'tuesday', short: 'T' },
  { key: 'wednesday', short: 'W' },
  { key: 'thursday', short: 'T' },
  { key: 'friday', short: 'F' },
  { key: 'saturday', short: 'S' },
];

const browserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

const formatLastRun = (iso) => {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const mins = Math.floor((now - then) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

export default function AutoSyncCard() {
  const { state, setAutoDispatchSchedule } = useApp();
  const { autoDispatch } = state;
  const toast = useToast();

  const [enabled, setEnabled] = useState(false);
  const [kind, setKind] = useState('weekly');
  const [days, setDays] = useState(['saturday']);
  const [time, setTime] = useState('18:00');
  const [saving, setSaving] = useState(false);
  const { plan, enrolled } = usePricingV2();
  const gate = autoSyncGate(plan, autoDispatch.enabled);

  useEffect(() => {
    setEnabled(autoDispatch.enabled);
    if (autoDispatch.schedule) {
      setKind(autoDispatch.schedule.kind || 'weekly');
      const scheduleDays = getScheduleDays(autoDispatch.schedule);
      setDays(scheduleDays.length > 0 ? scheduleDays : ['saturday']);
      setTime(autoDispatch.schedule.time || '18:00');
    }
  }, [autoDispatch.enabled, autoDispatch.schedule]);

  const toggleDay = (day) => {
    setDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const handleSave = async () => {
    if (enabled && kind === 'weekly' && days.length === 0) {
      toast.error('Select at least one day for weekly auto-sync');
      return;
    }
    setSaving(true);
    try {
      const schedule = {
        kind,
        ...(kind === 'weekly' ? { days } : {}),
        time,
        tz: browserTz(),
      };
      await setAutoDispatchSchedule({ enabled, schedule });
      toast.success(enabled ? 'Auto-sync schedule saved' : 'Auto-sync turned off');
    } catch (error) {
      toast.error(planErrorMessage(error, plan) ?? 'Failed to save auto-sync settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="elevated">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 squircle-sm ${enabled ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-100 dark:bg-elevated-dark-2'}`}>
              <Zap className={`w-6 h-6 ${enabled ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`} />
            </div>
            <div>
              <h3 className="text-heading font-semibold text-gray-900 dark:text-gray-100">
                Auto-sync
              </h3>
              <p className="text-body-sm text-gray-600 dark:text-gray-400 mt-1">
                Automatically deliver new and updated designs to your Media Stations
              </p>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-5">
          <button
            type="button"
            onClick={() => setEnabled(!enabled)}
            disabled={!enabled && !gate.canTurnOn}
            className="relative w-full h-12 squircle-sm bg-gray-200 dark:bg-elevated-dark-2 transition-colors touch-target disabled:opacity-60 disabled:cursor-not-allowed"
            aria-pressed={enabled}
          >
            <motion.div
              className={`absolute top-1 bottom-1 left-1 w-[calc(50%-0.25rem)] squircle-sm shadow-soft ${enabled ? 'bg-amber-500' : 'bg-white dark:bg-elevated-dark-3'}`}
              animate={{ x: enabled ? 'calc(100% + 0.5rem)' : 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
            <div className="relative flex items-center justify-around h-full px-4">
              <span className={`text-body-sm font-medium transition-colors ${!enabled ? 'text-gray-900' : 'text-gray-500'}`}>
                Off
              </span>
              <span className={`text-body-sm font-medium transition-colors ${enabled ? 'text-white' : 'text-gray-500'}`}>
                On
              </span>
            </div>
          </button>

          {!gate.canTurnOn && (
            <p data-testid="auto-sync-plan-notice" className="text-body-sm text-amber-800 dark:text-amber-300">
              {gate.message}
            </p>
          )}

          {plan?.autoSyncDesignLimit && (
            <p data-testid="auto-sync-design-limit-notice" className="text-body-sm text-gray-600 dark:text-gray-400">
              Your {plan.name} plan auto-syncs up to {plan.autoSyncDesignLimit} designs you choose ({enrolled.length} of {plan.autoSyncDesignLimit} chosen). Turn auto-sync on from a design's card on the dashboard.{' '}
              <Link to="/pricing" className="font-medium text-amber-700 dark:text-amber-400 underline">See plans</Link>
            </p>
          )}

          {enabled && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.2 }}
              className="space-y-4 overflow-hidden"
            >
              <div>
                <label className="block text-caption text-gray-500 dark:text-gray-400 mb-2">
                  Cadence
                </label>
                <div className="flex gap-2">
                  {['daily', 'weekly'].map(k => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setKind(k)}
                      className={`flex-1 py-2 px-4 squircle-sm text-body-sm font-medium transition-colors ${
                        kind === k
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-100 dark:bg-elevated-dark-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-elevated-dark-3'
                      }`}
                    >
                      {k === 'daily' ? 'Daily' : 'Weekly'}
                    </button>
                  ))}
                </div>
              </div>

              {kind === 'weekly' && (
                <div>
                  <label className="block text-caption text-gray-500 dark:text-gray-400 mb-2">
                    Day{days.length > 1 ? 's' : ''}
                  </label>
                  <div className="flex gap-1.5">
                    {DAYS.map(d => (
                      <button
                        key={d.key}
                        type="button"
                        onClick={() => toggleDay(d.key)}
                        className={`flex-1 h-10 squircle-sm text-body-sm font-semibold transition-colors ${
                          days.includes(d.key)
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-100 dark:bg-elevated-dark-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-elevated-dark-3'
                        }`}
                        title={d.key.charAt(0).toUpperCase() + d.key.slice(1)}
                      >
                        {d.short}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="auto-sync-time" className="block text-caption text-gray-500 dark:text-gray-400 mb-2">
                  Time of day
                </label>
                <input
                  id="auto-sync-time"
                  type="time"
                  value={time}
                  onChange={e => setTime(e.target.value)}
                  className="w-full px-4 py-2.5 squircle-sm bg-gray-100 dark:bg-elevated-dark-2 text-gray-900 dark:text-gray-100 text-body focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <p className="text-caption text-gray-500 dark:text-gray-400 mt-1.5">
                  Your timezone: {browserTz()}
                </p>
              </div>
            </motion.div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving}
            loading={saving}
            variant="primary"
            size="md"
            className="w-full sm:w-auto"
          >
            {saving ? 'Saving…' : 'Save schedule'}
          </Button>

          <div className="pt-2 border-t border-gray-200 dark:border-elevated-dark-2 space-y-1.5">
            <div className="flex items-center gap-2 text-body-sm text-gray-600 dark:text-gray-400">
              <Clock className="w-4 h-4" />
              <span>
                Last auto-push: <span className="font-medium text-gray-900 dark:text-gray-100">{formatLastRun(autoDispatch.lastRunAt)}</span>
              </span>
            </div>
            {enabled && (
              <p className="text-caption text-gray-500 dark:text-gray-400">
                Next: {describeSchedule({
                  kind,
                  ...(kind === 'weekly' ? { days } : {}),
                  time,
                })}
              </p>
            )}
            <p className="text-caption text-gray-500 dark:text-gray-400">
              TurboSync Pro continuously checks Canva for your design changes.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
